export const LETS_PLAY_GOLF_PLAYER_LIMIT = 4

export const GOLF_GAME_MODES = Object.freeze([
  { id: 'stroke_play', label: 'Stroke Play', description: 'Count every stroke across the round.' },
  { id: 'stableford', label: 'Stableford', description: 'Play for points on every hole.' },
])

export function createGolfSetup(member) {
  return {
    courseId: null,
    holes: null,
    teeId: null,
    gameMode: null,
    players: member?.id ? [{ type: 'member', memberId: String(member.id), displayName: member.displayName, isYou: true, order: 1 }] : [],
  }
}

export function addGolfPlayer(setup, player, limit = LETS_PLAY_GOLF_PLAYER_LIMIT) {
  if (!player || setup.players.length >= limit) return setup
  if (player.type === 'member' && setup.players.some((item) => item.type === 'member' && item.memberId === player.memberId)) return setup
  return { ...setup, players: [...setup.players, { ...player, order: setup.players.length + 1 }] }
}

export function removeGolfPlayer(setup, order) {
  const target = setup.players.find((player) => player.order === order)
  if (!target || target.isYou) return setup
  return { ...setup, players: setup.players.filter((player) => player.order !== order).map((player, index) => ({ ...player, order: index + 1 })) }
}

// Recent players will be projected from canonical round participation once that domain exists.
export async function loadRecentGolfPlayers({ clubCode, memberPortalApiGet }) {
  if (!memberPortalApiGet) return []
  const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/lets-play/golf/recent-players`)
  const payload = await response.json()
  return response.ok && Array.isArray(payload.data) ? payload.data.map((player) => ({ ...player, type: 'member' })) : []
}

export async function loadResumableGolfRounds({ clubCode, memberPortalApiGet }) {
  const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/lets-play/golf/rounds/in-progress`)
  const payload = await response.json()
  if (!response.ok || !Array.isArray(payload.data)) throw new Error(payload.error || 'rounds_load_failed')
  return payload.data
}

export async function abandonLetsPlayGolfRound({ clubCode, roundId, memberPortalApiPost }) {
  const response = await memberPortalApiPost(`/api/members-portal/${clubCode}/lets-play/golf/rounds/${roundId}/abandon`, {})
  const payload = await response.json()
  if (!response.ok) throw new Error(payload.error || 'round_abandon_failed')
  return payload.data
}

export function validateGolfSetup(setup, teesAvailable) {
  return Boolean(teesAvailable && setup.courseId && setup.holes && setup.gameMode && setup.teeId && setup.players[0]?.isYou)
}

// Persistence boundary: deliberately does not create a booking or client-side round.
export async function createLetsPlayGolfRound({ clubCode, setup, memberPortalApiPost }) {
  if (typeof memberPortalApiPost !== 'function') throw new Error('round_persistence_not_available')
  const response = await memberPortalApiPost(`/api/members-portal/${clubCode}/lets-play/golf/rounds`, setup)
  const payload = await response.json()
  if (!response.ok) throw new Error(payload.error || 'round_create_failed')
  return payload.data
}

export function classifyGolfMilestone(par, strokes) {
  if (!Number.isInteger(par) || !Number.isInteger(strokes) || strokes < 1) return null
  if (strokes === 1) return 'hole_in_one'
  const relative = strokes - par
  return relative === -3 ? 'albatross' : relative === -2 ? 'eagle' : relative === -1 ? 'birdie' : null
}

export const GOLF_CELEBRATIONS = Object.freeze({
  birdie: { title: 'BIRDIE', copy: ['Nicely judged.', 'Clean work.', 'One better.', 'That’ll play.'] },
  eagle: { title: 'EAGLE', copy: ['That’ll do.', 'Two better. Very tidy.', 'Now we’re talking.', 'Exceptional.'] },
  albatross: { title: 'ALBATROSS', copy: ['Well, that escalated.', 'Three under. Absurd.', 'You don’t see that every day.', 'Ridiculous. In the best way.'] },
  hole_in_one: { title: 'HOLE IN ONE', copy: ['One swing. That’s all it took.', 'Someone’s buying the drinks.', 'Straight in the history book.', 'Perfectly unnecessary second shot.'] },
})
